import { useCallback, useMemo, useState } from 'react'

import { JobSkillManagerSkillRow, type JobSkillDraft } from '@/components/employer/job-skill-manager-row'
import { SkillManagerAddRow } from '@/components/skills/skill-manager-parts'
import {
  buildJobSkillSuggestions,
  prepareAppendJobSkillDraft,
  prepareRenameJobSkillDraft,
  resolveSkillByName,
} from '@/lib/job-skill-suggestions'
import { useOrganisationSkills } from '@/hooks/use-organisation-skills'
import { useSkills } from '@/hooks/use-skills'

export function JobSkillsManagerBody({
  skills,
  onSkillsChange,
  organisationSlug,
  disabled,
  focusAdd = false,
}: {
  skills: JobSkillDraft[]
  onSkillsChange: (skills: JobSkillDraft[]) => void
  organisationSlug: string
  disabled?: boolean
  focusAdd?: boolean
}) {
  const { skills: catalogSkills, createSkillAsync, isCreatingSkill } = useSkills()
  const { skills: orgSkills, isLoading: orgSkillsLoading } = useOrganisationSkills(organisationSlug)

  const [addDraft, setAddDraft] = useState('')
  const [addError, setAddError] = useState<string | null>(null)
  const [editingSkillId, setEditingSkillId] = useState<string | null>(null)
  const [editDraft, setEditDraft] = useState('')
  const [editError, setEditError] = useState<string | null>(null)
  const [isSavingEdit, setIsSavingEdit] = useState(false)

  const resolvePool = useMemo(
    () => [...orgSkills, ...catalogSkills],
    [orgSkills, catalogSkills]
  )

  const excludeSkillIds = useMemo(() => skills.map((s) => s.skill_id), [skills])
  const excludeNames = useMemo(() => skills.map((s) => s.skill_name), [skills])

  const suggestionItems = useMemo(
    () =>
      buildJobSkillSuggestions({
        orgSkills,
        catalogSkills,
        excludeSkillIds,
      }).map((row) => ({ name: row.name })),
    [orgSkills, catalogSkills, excludeSkillIds]
  )

  const editSuggestionItems = useMemo(() => {
    if (!editingSkillId) return []
    return buildJobSkillSuggestions({
      orgSkills,
      catalogSkills,
      excludeSkillIds: excludeSkillIds.filter((id) => id !== editingSkillId),
    }).map((row) => ({ name: row.name }))
  }, [catalogSkills, editingSkillId, excludeSkillIds, orgSkills])

  const editExcludeNames = useMemo(() => {
    if (!editingSkillId) return excludeNames
    const editing = skills.find((s) => s.skill_id === editingSkillId)
    if (!editing) return excludeNames
    return excludeNames.filter(
      (name) => name.trim().toLowerCase() !== editing.skill_name.trim().toLowerCase()
    )
  }, [editingSkillId, excludeNames, skills])

  const applyResolvedSkill = useCallback(
    (editingId: string, resolved: { id: string; name: string }) => {
      if (skills.some((s) => s.skill_id === resolved.id && s.skill_id !== editingId)) {
        setEditError(`"${resolved.name}" is already on this job.`)
        return false
      }
      onSkillsChange(
        skills.map((s) =>
          s.skill_id === editingId
            ? { ...s, skill_id: resolved.id, skill_name: resolved.name }
            : s
        )
      )
      return true
    },
    [onSkillsChange, skills]
  )

  const resolveOrCreateSkill = useCallback(
    async (rawName: string): Promise<{ id: string; name: string }> => {
      const existing = resolveSkillByName(resolvePool, rawName)
      if (existing) return existing
      const created = await createSkillAsync(rawName.trim())
      return { id: created.id, name: created.name }
    },
    [createSkillAsync, resolvePool]
  )

  const handleAdd = useCallback(
    async (rawName: string) => {
      setAddError(null)
      const prep = prepareAppendJobSkillDraft(skills, rawName)
      if (!prep.ok) {
        if (prep.error) setAddError(prep.error)
        return
      }

      try {
        const resolved = await resolveOrCreateSkill(prep.trimmed)
        if (skills.some((s) => s.skill_id === resolved.id)) {
          setAddError(`"${resolved.name}" is already on this job.`)
          return
        }
        onSkillsChange([
          ...skills,
          {
            skill_id: resolved.id,
            skill_name: resolved.name,
            requirement_type: 'preferred',
          },
        ])
        setAddDraft('')
      } catch (err) {
        setAddError(err instanceof Error ? err.message : 'Could not add skill')
      }
    },
    [onSkillsChange, resolveOrCreateSkill, skills]
  )

  const startEditing = useCallback(
    (skill: JobSkillDraft) => {
      if (isSavingEdit) return
      setEditingSkillId(skill.skill_id)
      setEditDraft(skill.skill_name)
      setEditError(null)
      setAddError(null)
      setAddDraft('')
    },
    [isSavingEdit]
  )

  const cancelEditing = useCallback(() => {
    setEditingSkillId(null)
    setEditDraft('')
    setEditError(null)
    setIsSavingEdit(false)
  }, [])

  const handleSaveEdit = useCallback(
    async (rawName: string) => {
      setEditError(null)
      if (!editingSkillId || isSavingEdit) return

      const prep = prepareRenameJobSkillDraft(skills, editingSkillId, rawName)
      if (!prep.ok) {
        if (prep.error === null) {
          cancelEditing()
          return
        }
        setEditError(prep.error)
        return
      }

      const editingId = editingSkillId
      const existing = resolveSkillByName(resolvePool, prep.trimmed)
      if (existing) {
        if (applyResolvedSkill(editingId, existing)) {
          cancelEditing()
        }
        return
      }

      setIsSavingEdit(true)
      try {
        const resolved = await resolveOrCreateSkill(prep.trimmed)
        if (applyResolvedSkill(editingId, resolved)) {
          cancelEditing()
        }
      } catch (err) {
        setEditError(err instanceof Error ? err.message : 'Could not rename skill')
      } finally {
        setIsSavingEdit(false)
      }
    },
    [
      applyResolvedSkill,
      cancelEditing,
      editingSkillId,
      isSavingEdit,
      resolveOrCreateSkill,
      resolvePool,
      skills,
    ]
  )

  const handleToggleRequired = useCallback(
    (skillId: string, required: boolean) => {
      onSkillsChange(
        skills.map((s) =>
          s.skill_id === skillId
            ? { ...s, requirement_type: required ? 'required' : 'preferred' }
            : s
        )
      )
    },
    [onSkillsChange, skills]
  )

  const handleRemove = useCallback(
    (skillId: string) => {
      onSkillsChange(skills.filter((s) => s.skill_id !== skillId))
      if (editingSkillId === skillId) {
        cancelEditing()
      }
    },
    [cancelEditing, editingSkillId, onSkillsChange, skills]
  )

  const rowDisabled = disabled || isCreatingSkill || isSavingEdit

  return (
    <ul className="space-y-2">
      <SkillManagerAddRow
        inputId="job-skills-manager-add"
        draft={addDraft}
        error={addError}
        disabled={rowDisabled}
        autoFocus={focusAdd}
        excludeNames={excludeNames}
        suggestionItems={suggestionItems}
        suggestionsLoading={orgSkillsLoading}
        onDraftChange={(next) => {
          setAddDraft(next)
          setAddError(null)
        }}
        onAdd={(name) => {
          void handleAdd(name)
        }}
      />
      {skills.map((skill) => (
        <JobSkillManagerSkillRow
          key={skill.skill_id}
          skill={skill}
          isEditing={editingSkillId === skill.skill_id}
          editDraft={editDraft}
          editError={editError}
          editExcludeNames={editExcludeNames}
          editSuggestionItems={editSuggestionItems}
          suggestionsLoading={orgSkillsLoading}
          disabled={rowDisabled}
          onStartEdit={() => startEditing(skill)}
          onEditDraftChange={(next) => {
            setEditDraft(next)
            setEditError(null)
          }}
          onSaveEdit={(name) => {
            void handleSaveEdit(name)
          }}
          onCancelEdit={cancelEditing}
          onToggleRequired={(required) => handleToggleRequired(skill.skill_id, required)}
          onRemove={() => handleRemove(skill.skill_id)}
        />
      ))}
    </ul>
  )
}
